/**
 * InboxCard + per-tab empty state — extracted from inbox-view.tsx.
 * Behavior-preserving refactor; InboxView remains the orchestrator.
 * P2-10: park actions; stale/overdue meta. UX-B1: per-tab friendly empty states.
 */
import { useState, type ReactNode } from "react";
import { Clock, Inbox, CalendarCheck, Hourglass, Zap } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { InboxItem, InboxStatus } from "../types";
import { formatDate } from "../lib/date";
import { waitingOverdue } from "./inbox-derive";
import { IconButton } from "./bento-ui";
import { InboxClarifyModal } from "./inbox-clarify-modal";
import { WaitingModal } from "./inbox-waiting-modal";

type TabKey = InboxStatus;

// ── Per-tab empty state config ────────────────────────────────────────────────

interface TabEmptyConfig {
  icon: ReactNode;
  title: string;
  hint: string;
}

const TAB_EMPTY: Record<TabKey, TabEmptyConfig> = {
  inbox: {
    icon: <Inbox className="h-6 w-6" />,
    title: "Hộp thư đến trống 🎉",
    hint: "Quăng mọi thứ chợt nghĩ vào đây — không cần hoàn hảo, phân loại sau.",
  },
  someday: {
    icon: <CalendarCheck className="h-6 w-6" />,
    title: "Chưa có việc “để dành”",
    hint: "Những ý tưởng chưa muốn làm ngay, bấm Someday từ Inbox để gửi vào đây.",
  },
  waiting: {
    icon: <Hourglass className="h-6 w-6" />,
    title: "Không có việc đang chờ ai",
    hint: "Khi cần đợi phản hồi từ ai đó, bấm “Đang chờ” từ Inbox và ghi lại để không quên.",
  },
};

// ── Per-tab empty state ───────────────────────────────────────────────────────

export function InboxEmptyState({ tabKey }: { tabKey: TabKey }) {
  const cfg = TAB_EMPTY[tabKey];
  return (
    <div className="rounded-2xl border border-dashed border-ink-200 bg-white/60 py-10 text-center">
      <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-2xl bg-brand-50 text-brand-400">
        {cfg.icon}
      </div>
      <p className="font-display text-[14px] font-bold text-ink-700">{cfg.title}</p>
      <p className="mx-auto mt-1 max-w-xs text-[12px] leading-relaxed text-ink-400">{cfg.hint}</p>
    </div>
  );
}

// ── Single inbox card ─────────────────────────────────────────────────────────

export function InboxCard({
  item,
  api,
  onDelete,
  today,
  onStartQueue,
  inboxItems,
}: {
  item: InboxItem;
  api: LifeOsApi;
  onDelete: (fn: () => void) => void;
  today: string;
  /** A7: when present, clicking Phân loại starts a bulk queue at this item's index. */
  onStartQueue?: (startIndex: number) => void;
  /** A7: the current ordered inbox list — needed to find this item's index. */
  inboxItems?: InboxItem[];
}) {
  // showClarify is used only for non-inbox-tab (someday/waiting) single-item mode.
  const [showClarify, setShowClarify] = useState(false);
  const [showWaitingModal, setShowWaitingModal] = useState(false);
  const status = item.status ?? "inbox";
  const overdue = waitingOverdue(item, today);

  const parkSomeday = () => api.actions.updateInboxItem(item.id, { status: "someday" });
  const parkWaiting = (waitingFor: string, waitingUntil: string) =>
    api.actions.updateInboxItem(item.id, {
      status: "waiting",
      ...(waitingFor.trim() ? { waitingFor: waitingFor.trim() } : {}),
      ...(waitingUntil ? { waitingUntil } : {}),
    });
  const backToInbox = () => api.actions.updateInboxItem(item.id, { status: "inbox" });

  return (
    <>
      <div data-testid={`inbox-card-${item.id}`} className={`group rounded-3xl bg-white p-4 ${overdue ? "ring-2 ring-red-300" : ""}`}>
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-ink-200/50 text-ink-700">
            <Zap className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold leading-snug">{item.text}</p>
            {item.hint && (
              <p className="mt-0.5 text-[11px] uppercase tracking-wide text-ink-300">{item.hint}</p>
            )}
            {/* Waiting meta: who + follow-up date */}
            {status === "waiting" && (item.waitingFor || item.waitingUntil) && (
              <div className={`mt-1 flex items-center gap-1 text-[11px] ${overdue ? "text-red-500 font-semibold" : "text-ink-400"}`}>
                <Clock className="h-3 w-3" />
                {item.waitingFor && <span>{item.waitingFor}</span>}
                {item.waitingUntil && (
                  <span>{item.waitingFor ? " · " : ""}theo dõi {formatDate(item.waitingUntil)}{overdue ? " (quá hạn)" : ""}</span>
                )}
              </div>
            )}
          </div>
          <IconButton icon="delete" label="Xoá" onClick={() => onDelete(() => api.actions.remove("inbox", item.id))} />
        </div>

        {/* Actions */}
        <div className="mt-3 flex flex-wrap items-center gap-2 pl-12">
          {status === "inbox" && (
            <>
              <button
                onClick={() => {
                  if (onStartQueue && inboxItems) {
                    // A7: start queue from this item's position
                    const idx = inboxItems.findIndex((i) => i.id === item.id);
                    onStartQueue(idx >= 0 ? idx : 0);
                  } else {
                    setShowClarify(true);
                  }
                }}
                className="gbrand rounded-lg px-3 py-1.5 text-[12px] font-bold text-white"
              >
                Phân loại
              </button>
              <span className="mx-1 h-4 w-px bg-ink-200" />
              {/* Park actions (P2-10) */}
              <button
                data-testid={`park-someday-${item.id}`}
                onClick={parkSomeday}
                title="Để dành xem sau (Someday/Maybe)"
                className="rounded-lg bg-ink-100 px-3 py-1.5 text-[12px] font-bold text-ink-600 hover:bg-ink-200/60"
              >
                Someday
              </button>
              <button
                data-testid={`park-waiting-${item.id}`}
                onClick={() => setShowWaitingModal(true)}
                title="Đang chờ ai đó / điều gì đó"
                className="rounded-lg bg-ink-100 px-3 py-1.5 text-[12px] font-bold text-ink-600 hover:bg-ink-200/60"
              >
                Đang chờ
              </button>
              <span className="mx-1 h-4 w-px bg-ink-200" />
              <button
                onClick={() => onDelete(() => api.actions.remove("inbox", item.id))}
                className="rounded-lg bg-ink-200/50 px-3 py-1.5 text-[12px] font-bold text-ink-600"
              >
                Lưu trữ
              </button>
            </>
          )}
          {(status === "someday" || status === "waiting") && (
            <>
              <button
                onClick={() => setShowClarify(true)}
                className="gbrand rounded-lg px-3 py-1.5 text-[12px] font-bold text-white"
              >
                Phân loại
              </button>
              <button
                onClick={backToInbox}
                title="Đưa lại Inbox"
                className="rounded-lg bg-ink-100 px-3 py-1.5 text-[12px] font-bold text-ink-600 hover:bg-ink-200/60"
              >
                Đưa lại Inbox
              </button>
              <button
                onClick={() => onDelete(() => api.actions.remove("inbox", item.id))}
                className="rounded-lg bg-ink-200/50 px-3 py-1.5 text-[12px] font-bold text-ink-600"
              >
                Xoá
              </button>
            </>
          )}
        </div>
      </div>

      {showClarify && (
        <InboxClarifyModal item={item} api={api} onClose={() => setShowClarify(false)} />
      )}

      {showWaitingModal && (
        <WaitingModal
          onConfirm={(who, until) => {
            parkWaiting(who, until);
            setShowWaitingModal(false);
          }}
          onClose={() => setShowWaitingModal(false)}
        />
      )}
    </>
  );
}
